import { useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { RuleForm } from '@/components/settings/RuleForm';
import { useFolderStore } from '@/stores/folderStore';
import { useRuleStore } from '@/stores/ruleStore';
import { useUIStore } from '@/stores/uiStore';

export function RuleModalHost() {
  const folders = useFolderStore((state) => state.folders);
  const createRule = useRuleStore((state) => state.createRule);
  const updateRule = useRuleStore((state) => state.updateRule);
  const ruleEditorOpen = useUIStore((state) => state.ruleEditorOpen);
  const setRuleEditorOpen = useUIStore((state) => state.setRuleEditorOpen);
  const editingRule = useUIStore((state) => state.editingRule);
  const setEditingRule = useUIStore((state) => state.setEditingRule);
  const ruleEditorAccountId = useUIStore((state) => state.ruleEditorAccountId);
  const setRuleEditorAccountId = useUIStore((state) => state.setRuleEditorAccountId);
  const setGlobalError = useUIStore((state) => state.setGlobalError);

  const handleSaveRule = useCallback(
    async (data: Parameters<typeof updateRule>[2]) => {
      if (!ruleEditorAccountId) {
        setGlobalError('Select an account before editing rules.');
        return;
      }
      try {
        if (editingRule) {
          await updateRule(ruleEditorAccountId, editingRule.id, data);
        } else {
          if (!data.name || !data.conditions || !data.actions) {
            throw new Error('Rule details are incomplete');
          }
          await createRule(ruleEditorAccountId, {
            name: data.name,
            conditions: data.conditions,
            actions: data.actions,
          });
        }
        setRuleEditorOpen(false);
        setEditingRule(null);
      } catch (error) {
        console.error('Failed to save rule', error);
        setGlobalError('Failed to save rule');
      }
    },
    [createRule, editingRule, ruleEditorAccountId, setEditingRule, setGlobalError, setRuleEditorOpen, updateRule]
  );

  const handleClose = useCallback(() => {
    setRuleEditorOpen(false);
    setEditingRule(null);
    setRuleEditorAccountId(null);
  }, [setEditingRule, setRuleEditorAccountId, setRuleEditorOpen]);

  return (
    <Dialog open={ruleEditorOpen} onOpenChange={handleClose}>
      <DialogContent className="w-full max-w-3xl p-0 overflow-hidden bg-background h-[85vh] max-h-[800px]">
        <RuleForm
          rule={editingRule}
          folders={folders}
          onSave={handleSaveRule}
          onCancel={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
}
